import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date)) return '-';
  return date.toLocaleDateString('it-IT');
};

const toTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date)) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const buildDateTimeValue = (baseDate, timeValue) => {
  if (timeValue === undefined) return undefined;
  if (timeValue === null || timeValue === '') return null;
  const base = new Date(baseDate);
  const isoDate = Number.isNaN(base) ? new Date().toISOString().slice(0, 10) : base.toISOString().slice(0, 10);
  return `${isoDate}T${timeValue}:00`;
};

const GestionePresenze = ({ ruoloCorrente = 'admin' }) => {
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const defaultEnd = today.toISOString().slice(0, 10);

  const [presenze, setPresenze] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [filters, setFilters] = useState({ startDate: defaultStart, endDate: defaultEnd, sedeId: '' });
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    caricaSedi();
  }, []);

  useEffect(() => {
    caricaPresenze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.sedeId]);

  const caricaSedi = async () => {
    try {
      const res = await api.get('/sedi');
      setSedi(res.data || []);
    } catch (error) {
      console.error('Errore caricamento sedi', error);
    }
  };

  const caricaPresenze = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.sedeId) params.sedeId = filters.sedeId;
      const res = await api.get('/presenze-gestione', { params });
      setPresenze(res.data || []);
    } catch (error) {
      console.error('Errore caricamento presenze', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFieldChange = (row, field, timeValue) => {
    const presenzaId = row.presenza_id;
    const normalized = timeValue === '' ? '' : timeValue;
    const dateValue = buildDateTimeValue(row.data_presenza, timeValue);
    setEditing((prev) => ({
      ...prev,
      [presenzaId]: {
        ...prev[presenzaId],
        [field]: normalized,
      },
    }));

    setPresenze((prev) =>
      prev.map((row) =>
        row.presenza_id === presenzaId
          ? { ...row, [field]: dateValue }
          : row
      )
    );
  };

  const handleReset = (presenzaId) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[presenzaId];
      return next;
    });
  };

  const handleSave = async (presenzaId) => {
    const edit = editing[presenzaId];
    if (!edit) return;
    const row = presenze.find((p) => p.presenza_id === presenzaId);
    if (!row) return;
    const payload = {};
    Object.entries(edit).forEach(([field, timeValue]) => {
      if (timeValue === '') {
        payload[field] = null;
      } else if (timeValue !== undefined) {
        payload[field] = buildDateTimeValue(row.data_presenza, timeValue);
      }
    });
    if (Object.keys(payload).length === 0) return;
    try {
      await api.put(`/presenze/${presenzaId}`, payload);
      handleReset(presenzaId);
      caricaPresenze();
      alert('✅ Presenza aggiornata');
    } catch (error) {
      alert(error.response?.data?.message || "Errore durante l'aggiornamento");
    }
  };

  const presenzeFiltrate = useMemo(() => {
    if (!filters.sedeId) return presenze;
    return presenze.filter((p) => String(p.sede_id || '') === String(filters.sedeId));
  }, [presenze, filters.sedeId]);

  return (
    <div>
      <h2>🕒 Gestione Presenze</h2>

      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
        <label>
          Dal:
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
        </label>
        <label>
          Al:
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
        </label>
        <label>
          Sede:
          <select name="sedeId" value={filters.sedeId} onChange={handleFilterChange}>
            <option value="">Tutte</option>
            {sedi.map((sede) => (
              <option key={sede.sede_id} value={sede.sede_id}>
                {sede.nome}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Caricamento presenze...</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Sede</th>
                <th>Data</th>
                <th>Entrata Mattina</th>
                <th>Uscita Mattina</th>
                <th>Entrata Pomeriggio</th>
                <th>Uscita Pomeriggio</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {presenzeFiltrate.map((row) => {
                const edit = editing[row.presenza_id] || {};
                return (
                  <tr key={row.presenza_id}>
                    <td>{row.nome}</td>
                    <td>{row.sede_nome || '-'}</td>
                    <td>{formatDate(row.data_presenza)}</td>
                    <td>
                      <input
                        type="time"
                        value={edit.entrata_matt ?? toTimeInput(row.entrata_matt)}
                        onChange={(e) => handleFieldChange(row, 'entrata_matt', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={edit.uscita_matt ?? toTimeInput(row.uscita_matt)}
                        onChange={(e) => handleFieldChange(row, 'uscita_matt', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={edit.entrata_pom ?? toTimeInput(row.entrata_pom)}
                        onChange={(e) => handleFieldChange(row, 'entrata_pom', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={edit.uscita_pom ?? toTimeInput(row.uscita_pom)}
                        onChange={(e) => handleFieldChange(row, 'uscita_pom', e.target.value)}
                      />
                    </td>
                    <td>
                      <button onClick={() => handleSave(row.presenza_id)} disabled={!editing[row.presenza_id]}>
                        💾 Salva
                      </button>
                      <button
                        onClick={() => handleReset(row.presenza_id)}
                        style={{ marginLeft: '6px' }}
                        disabled={!editing[row.presenza_id]}
                      >
                        ↩️ Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
              {presenzeFiltrate.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    Nessuna presenza trovata per i filtri selezionati.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GestionePresenze;
